import React from "react";
import { useAppContext } from "../context/AppContext";

function AuthPage({ authError, onBack, hasPendingResume }) {
  const { theme, toggleTheme } = useAppContext();

  const handleGoogleLogin = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    window.location.href = `${backendUrl}/auth/google`;
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-between overflow-hidden relative transition-colors duration-300"
      style={{
        background: theme === "dark"
          ? "linear-gradient(160deg, #0B1220 0%, #14213D 45%, #0B1220 100%)"
          : "linear-gradient(160deg, #FAFAF8 0%, #EBEBE6 100%)"
      }}
    >
      <h1 className="sr-only">Resumebar — Free AI Resume Builder</h1>

      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div style={{
          position: "absolute",
          top: "25%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 550,
          height: 550,
          background: theme === "dark"
            ? "radial-gradient(circle, rgba(252,163,17,0.12) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(252,163,17,0.06) 0%, transparent 65%)",
          borderRadius: "50%"
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: theme === "dark"
            ? "radial-gradient(circle, rgba(252,163,17,0.04) 1px, transparent 1px)"
            : "radial-gradient(circle, rgba(20,33,61,0.03) 1px, transparent 1px)",
          backgroundSize: "44px 44px"
        }} />
      </div>

      {/* ── HEADER ── */}
      <header
        className="relative z-30 flex items-center justify-between px-5 sm:px-10 py-4 border-b transition-colors duration-300"
        style={{
          borderColor: theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(20,33,61,0.06)",
          background: theme === "dark" ? "rgba(11,18,32,0.5)" : "rgba(250,250,248,0.5)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)"
        }}
      >
        <div className="flex items-center gap-2.5">
          {onBack && (
            <button
              onClick={onBack}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style={{
                border: 'none',
                background: 'transparent',
                color: theme === "dark" ? '#B9C2D0' : '#5B6472',
                cursor: 'pointer',
                fontSize: 18
              }}
              title="Back to previous page"
              onMouseEnter={e => e.currentTarget.style.background = theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(20,33,61,0.05)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              ←
            </button>
          )}
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img
              src={theme === "dark" ? "/resumebar-logo-dark.svg" : "/resumebar-logo-horizontal.svg"}
              alt="Resumebar Logo"
              style={{ height: "26px" }}
              className="object-contain select-none cursor-pointer"
              onClick={onBack}
            />
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background:"rgba(252,163,17,0.12)", border:"1px solid rgba(252,163,17,0.25)", color:"#FCA311" }}>BETA</span>
          </div>
        </div>

        {/* Theme toggle in header */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 select-none text-lg hover:scale-105"
            style={{
              border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(20,33,61,0.1)'}`,
              color: theme === 'dark' ? '#B9C2D0' : '#5B6472',
              background: 'transparent',
              cursor: 'pointer'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(20,33,61,0.04)';
              e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#14213D';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme === 'dark' ? '#B9C2D0' : '#5B6472';
            }}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀' : '☽'}
          </button>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-3 sm:px-6 py-6 sm:py-10">
        <div className="w-full max-w-[450px] space-y-6">
          
          {/* Main Google-like Card */}
          <div
            className="relative rounded-2xl p-5 sm:p-10 transition-all duration-300"
            style={{ 
              background: theme === "dark" ? "#0B1220" : "#ffffff", 
              border: theme === "dark" ? "1px solid rgba(252, 163, 17, 0.15)" : "1px solid #B9C2D0", 
              boxShadow: theme === "dark" 
                ? "0 12px 40px rgba(0,0,0,0.3), 0 0 1px rgba(255,255,255,0.1) inset" 
                : "0 12px 40px rgba(20,33,61,0.06), 0 0 1px rgba(0,0,0,0.04) inset" 
            }}
          >
            {/* Top Brand gradient accent line */}
            <div className="absolute top-0 left-0 right-0 h-[5px] rounded-t-2xl"
              style={{
                background: "linear-gradient(90deg, #14213D, #FCA311, #14213D)"
              }}
            />

            {/* Resumebar Brand Logo centered directly */}
            <div className="flex justify-center mb-6 mt-2">
              <img 
                src="/resumebar-logo-stacked.svg" 
                alt="Resumebar Logo" 
                style={{ width: "120px", height: "120px" }} 
                className="object-contain select-none" 
              />
            </div>

            <div className="text-center mb-6">
              {hasPendingResume ? (
                <>
                  <h2 className="font-semibold text-2xl tracking-tight" style={{ color: theme === "dark" ? "#FAFAF8" : "#14213D", fontFamily: "var(--font-display)" }}>
                    Almost there!
                  </h2>
                  <p className="text-sm mt-2.5 transition-colors" style={{ color: theme === "dark" ? "#B9C2D0" : "#5B6472" }}>
                    Log in to save and generate your resume. Your data is kept safe.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-semibold transition-colors"
                    style={{
                      background: theme === "dark" ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)',
                      border: theme === "dark" ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(16,185,129,0.2)',
                      color: theme === "dark" ? '#34d399' : '#065f46'
                    }}
                  >
                    ✓ Your resume data is saved — won't be lost
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-semibold text-2xl tracking-tight" style={{ color: theme === "dark" ? "#FAFAF8" : "#14213D", fontFamily: "var(--font-display)" }}>
                    Log in to Resumebar
                  </h2>
                  <p className="text-sm mt-2.5 transition-colors" style={{ color: theme === "dark" ? "#B9C2D0" : "#5B6472" }}>
                    Build your ATS-optimized resume with AI
                  </p>
                </>
              )}
            </div>

            {authError && (
              <div className="mb-5 flex items-start gap-2.5 rounded-xl px-4 py-3" style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)" }}>
                <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color:"#ef4444" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="text-xs font-medium leading-relaxed" style={{ color:"#ef4444", margin: 0 }}>{authError}</p>
              </div>
            )}

            {/* Google Sign-in Button with official dark/light specs */}
            <button
              id="btn-google-signin"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 rounded-lg px-5 py-3 font-semibold text-sm transition-all duration-200 border cursor-pointer select-none active:scale-[0.98]"
              style={{
                background: theme === 'dark' ? '#14213D' : '#ffffff',
                color: theme === 'dark' ? '#FAFAF8' : '#14213D',
                borderColor: theme === 'dark' ? '#FCA311' : '#B9C2D0',
                fontFamily: "inherit"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = theme === 'dark' ? '#1E2B4A' : '#FAFAF8';
                e.currentTarget.style.borderColor = theme === 'dark' ? '#FCA311' : '#FCA311';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = theme === 'dark' ? '#14213D' : '#ffffff';
                e.currentTarget.style.borderColor = theme === 'dark' ? '#FCA311' : '#B9C2D0';
              }}
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Privacy details list container */}
            <div className="rounded-xl p-4.5 mt-6 border transition-all"
              style={{
                background: theme === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)",
                borderColor: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)"
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-[11px] font-medium tracking-wide uppercase" style={{ color: theme === "dark" ? "#B9C2D0" : "#5B6472" }}>
                  secure · private · free
                </span>
              </div>

              <ul className="space-y-2.5">
                {[
                  "Only accesses your name, email & profile picture",
                  "Resume data stored privately, linked to your account",
                  "We never share your data with third parties"
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <svg className="w-3.5 h-3.5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" style={{ color:"#34d399" }}>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <p className="text-xs transition-colors" style={{ color: theme === "dark" ? "#B9C2D0" : "#5B6472" }}>{p}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 w-full max-w-[450px] mx-auto px-4 py-4 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          {/* Language selector mock */}
          <div className="flex items-center gap-1 text-[11px] cursor-pointer transition-colors"
            style={{ color: theme === 'dark' ? '#B9C2D0' : '#5B6472' }}
            onMouseEnter={e => e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#14213D'}
            onMouseLeave={e => e.currentTarget.style.color = theme === 'dark' ? '#B9C2D0' : '#5B6472'}
          >
            <span>English (United States)</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>

          {/* Google style links */}
          <div className="flex gap-4 text-[11px]" style={{ color: theme === 'dark' ? '#B9C2D0' : '#5B6472' }}>
            <a href="/" onClick={e => e.preventDefault()} className="hover:underline transition-all" style={{ color: 'inherit' }}>Help</a>
            <a href="/" onClick={e => e.preventDefault()} className="hover:underline transition-all" style={{ color: 'inherit' }}>Privacy</a>
            <a href="/" onClick={e => e.preventDefault()} className="hover:underline transition-all" style={{ color: 'inherit' }}>Terms</a>
          </div>
        </div>
        <p className="text-[10px] text-center transition-colors" style={{ color: theme === "dark" ? "#5B6472" : "#9CA3AF" }}>
          © {new Date().getFullYear()} resumebar.in — Secure Google Sign-In.
        </p>
      </footer>
    </div>
  );
}

export default AuthPage;
